import { useRef, useState, useEffect } from 'react';
import { trackView } from '@/lib/tracking';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { idOrSlugColumn } from '@/lib/idOrSlug';
import { SEOHead } from '@/components/SEOHead';
import { Layout } from '@/components/layout/Layout';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, ChevronRight, MapPin, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

import { ProjectImageGallery } from '@/components/project/ProjectImageGallery';
import { ProjectOverview } from '@/components/project/ProjectOverview';
import { ProjectAboutSection } from '@/components/project/ProjectAboutSection';
import { ProjectFeatures } from '@/components/project/ProjectFeatures';
import { ProjectDevelopmentUpdates } from '@/components/project/ProjectDevelopmentUpdates';
import { ProjectFloorPlans } from '@/components/project/ProjectFloorPlans';
import { ProjectPaymentPlan } from '@/components/project/ProjectPaymentPlan';
import { ProjectLocation } from '@/components/project/ProjectLocation';
import { ProjectDeveloperInfo } from '@/components/project/ProjectDeveloperInfo';
import { ProjectContactSidebar } from '@/components/project/ProjectContactSidebar';
import { MobileStickyHeader } from '@/components/project/MobileStickyHeader';
import { MobileSectionTabs } from '@/components/project/MobileSectionTabs';
import { MobileBottomCTA } from '@/components/project/MobileBottomCTA';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatPrice as fmtPrice, formatPriceCompact } from '@/lib/formatPrice';

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const heroRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*, project_images(*), developer_profiles(*)')
        .eq(idOrSlugColumn(id), id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (!project?.id) return;
    trackView({ listingType: 'project', listingId: project.id, city: project.city });
  }, [project?.id]);

  const getName = (val: any) => typeof val === 'object' ? (val?.[lang] || val?.en || '') : (val || '');

  const { displayCurrency, rate } = useCurrency();
  const formatPrice = (min: number | null, max: number | null, currency: string) => {
    const fmt = (n: number) => fmtPrice(n, currency, displayCurrency, rate);
    if (min && max) return `${fmt(min)} – ${fmt(max)}`;
    if (min) return `From ${fmt(min)}`;
    if (max) return `Up to ${fmt(max)}`;
    return 'Price TBD';
  };

  const formatPriceShort = (min: number | null, max: number | null, currency: string) => {
    const fmt = (n: number) => formatPriceCompact(n, currency, displayCurrency, rate);
    if (min && max) return `${fmt(min)} – ${fmt(max)}`;
    if (min) return `From ${fmt(min)}`;
    if (max) return fmt(max);
    return 'Price TBD';
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="container py-8 space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] rounded-xl" />
          <div className="grid md:grid-cols-3 gap-6">
            <Skeleton className="h-60 md:col-span-2" />
            <Skeleton className="h-60" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg text-muted-foreground">Project not found</p>
          <Button variant="link" asChild className="mt-4"><Link to="/projects">Back to Projects</Link></Button>
        </div>
      </Layout>
    );
  }

  const name = getName(project.name);
  const desc = getName(project.description);
  const images = (project.project_images || []).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
  const floorPlanImages = images.filter((i: any) => i.is_floor_plan);
  const dev = project.developer_profiles;
  const thumbUrl = images[0]?.url;

  const statusColors: Record<string, string> = {
    upcoming: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    under_construction: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  };

  const sectionTabs = [
    { id: 'overview', label: 'Overview' },
    ...(desc ? [{ id: 'about', label: 'About' }] : []),
    { id: 'features', label: 'Features' },
    { id: 'updates', label: 'Updates' },
    ...(floorPlanImages.length > 0 ? [{ id: 'floor-plans', label: 'Master Plans' }] : []),
    { id: 'payment-plan', label: 'Payment Plan' },
    { id: 'location', label: 'Location' },
    ...(dev ? [{ id: 'developer', label: 'Developer' }] : []),
  ];

  return (
    <Layout>
      <SEOHead
        title={`${name} — New Development in ${project.city}`}
        description={desc ? String(desc).slice(0, 155) : `New real estate project in ${project.city}. ${project.total_units ? project.total_units + ' units. ' : ''}${formatPrice(project.price_min, project.price_max, project.currency || 'ETB')}`}
        canonical={`/projects/${id}`}
        ogType="article"
        ogImage={{ type: 'project', id: id!, badge: 'New Project' }}
      />

      {/* Mobile sticky chrome */}
      <MobileStickyHeader
        name={name}
        city={project.city || ''}
        price={formatPriceShort(project.price_min, project.price_max, project.currency || 'ETB')}
        thumbUrl={thumbUrl}
        triggerRef={heroRef}
        onVisibilityChange={setHeaderVisible}
      />
      <MobileSectionTabs tabs={sectionTabs} visible={true} headerVisible={headerVisible} />

      {/* Desktop breadcrumb */}
      <div className="hidden lg:block bg-muted/40 border-b">
        <div className="container py-3">
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/projects" className="hover:text-primary">New Projects</Link>
            <ChevronRight className="h-3 w-3" />
            {project.city && <><span>{project.city}</span><ChevronRight className="h-3 w-3" /></>}
            <span className="text-foreground font-medium truncate max-w-[200px]">{name}</span>
          </nav>
        </div>
      </div>

      {/* Spacer for mobile sticky tabs (header is conditional, tabs always visible) */}
      <div className="lg:hidden h-[45px]" />

      <div ref={heroRef} className="container px-4 lg:px-8 py-3 lg:py-5 pb-24 lg:pb-6 max-w-full overflow-x-hidden">
        {/* Desktop title row */}
        <div className="hidden lg:flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
          <div className="flex items-start gap-4">
            {dev?.logo_url && (
              <div className="h-14 w-14 rounded-lg border bg-card overflow-hidden shrink-0">
                <img src={dev.logo_url} alt="" className="h-full w-full object-cover" />
              </div>
            )}
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-bold">{name}</h1>
              <div className="flex items-center gap-2 mt-1 text-muted-foreground text-sm">
                <MapPin className="h-4 w-4 text-primary" />
                <span>{project.address || project.city}{project.sub_city ? `, ${project.sub_city}` : ''}</span>
              </div>
              {project.reference_code && (
                <div className="mt-1 text-[11px] font-mono text-muted-foreground">
                  ID: {project.reference_code.toUpperCase()}
                </div>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xl md:text-2xl font-bold">
              {formatPrice(project.price_min, project.price_max, project.currency || 'ETB')}
            </div>
            <Badge className={`mt-1 text-xs ${statusColors[project.status || ''] || 'bg-muted'}`}>
              {project.status?.replace('_', ' ')}
            </Badge>
            {project.completion_date && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1 justify-end">
                <Calendar className="h-3 w-3" />
                Completion: {new Date(project.completion_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
            )}
          </div>
        </div>

        <div className="mb-4 lg:mb-5">
          <ProjectImageGallery images={images} name={name} developerLogo={dev?.logo_url} developerName={dev?.company_name} />
        </div>

        {/* Mobile compact title block */}
        <div className="lg:hidden mb-4 min-w-0">
          <h1 className="font-display text-xl font-bold leading-tight break-words">{name}</h1>
          <div className="flex items-start gap-1.5 mt-1 text-muted-foreground text-xs">
            <MapPin className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
            <span className="break-words min-w-0">{project.address || project.city}{project.sub_city ? `, ${project.sub_city}` : ''}</span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
            <div className="text-base font-bold text-primary break-words min-w-0 flex-1">
              {formatPrice(project.price_min, project.price_max, project.currency || 'ETB')}
            </div>
            <Badge className={`text-[10px] shrink-0 ${statusColors[project.status || ''] || 'bg-muted'}`}>
              {project.status?.replace('_', ' ')}
            </Badge>
          </div>
          {project.completion_date && (
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1">
              <Calendar className="h-3 w-3 shrink-0" />
              <span className="break-words">Completion: {new Date(project.completion_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
            </div>
          )}
          {project.reference_code && (
            <div className="mt-1 text-[11px] font-mono text-muted-foreground">
              ID: {project.reference_code.toUpperCase()}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          <div
            className="lg:col-span-2 space-y-5 lg:space-y-7 [&>section]:scroll-mt-[var(--sticky-offset)] lg:[&>section]:scroll-mt-24"
            style={{ ['--sticky-offset' as any]: headerVisible ? '102px' : '50px' }}
          >
            <section id="overview">
              <ProjectOverview project={project} developerName={dev?.company_name || 'N/A'} formatPrice={formatPrice} />
            </section>

            {desc && (
              <section id="about">
                <h2 className="font-display text-lg lg:text-xl font-bold mb-3 lg:mb-4">About This Project</h2>
                <ProjectAboutSection description={String(desc)} />
              </section>
            )}

            <section id="features"><ProjectFeatures /></section>
            <section id="updates"><ProjectDevelopmentUpdates /></section>
            {floorPlanImages.length > 0 && (
              <section id="floor-plans"><ProjectFloorPlans floorPlanImages={floorPlanImages} /></section>
            )}
            <section id="payment-plan"><ProjectPaymentPlan /></section>
            <section id="location">
              <ProjectLocation city={project.city} subCity={project.sub_city} address={project.address}
                latitude={project.latitude ? Number(project.latitude) : null}
                longitude={project.longitude ? Number(project.longitude) : null} />
            </section>
            {dev && (
              <section id="developer">
                <ProjectDeveloperInfo developer={dev} getName={getName} />
              </section>
            )}
          </div>

          <div className="lg:col-span-1">
            <ProjectContactSidebar projectId={id!} referenceCode={project.reference_code} />
          </div>
        </div>
      </div>

      {/* Mobile bottom CTA */}
      <MobileBottomCTA projectId={id!} referenceCode={project.reference_code} phone={dev?.phone} whatsapp={dev?.phone?.replace(/\D/g, '')} />
    </Layout>
  );
}
